import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import CookieJs from "js-cookie";
import { get } from "lodash";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import Breadcrumb from "../components/Breadcrumb";
import MainLayout from "../components/MainLayout";
import ProtectedPermissionPage from "../components/ProtectedPermissionPage";
import useNotification from "../hooks/useNotification";
import event from "../services/event";
import role from "../services/role";
import userService from "../services/users";
import queryDefault from "../utils/queryDefault";

export default function UserCreate() {
  const notification = useNotification();
  const navigate = useNavigate();
  const user = JSON.parse(CookieJs.get("USER") || "");
  const [showEvent, setShowEvent] = useState(true);
  const schema = yup.object().shape({
    name: yup
      .string()
      .min(5)
      .required("name is a required field and length > 5"),
    role_id: yup.string().required("role is a required field"),
    username: yup
      .string()
      .min(5)
      .matches(/^[a-z0-9]+$/, "lowercase, no symbol and space")
      .required("username is a required field and length > 5"),
  });
  const form = useForm({
    defaultValues: {
      name: "",
      username: "",
      phone_number: "",
      event_id: "",
      role_id: "",
      admin_name: user.username,
      specific_user: user.role_id === 7 ? user.id : null,
    },
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    const subscription = form.watch((value) => {
      if (Number(value.role_id) === 7) {
        setShowEvent(false);
        if (value.event_id !== "") {
          form.setValue("event_id", "");
        }
      } else {
        setShowEvent(true);
      }
    });
    return () => subscription.unsubscribe?.();
  }, [form]);

  const createUser = useMutation("create_user", userService.createUser, {
    onSuccess: () => {
      navigate("/app/users/");

      notification.onOpen({
        message: "User was successfully created!",
        type: "success",
        position: "top",
      });
    },
    onError: () => {
      notification.onOpen({
        message: "Something went wrong",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const onSubmit = (values: any) => {
    createUser.mutate({
      data: values,
    });
  };

  const listEvent = useQuery("list-event", () =>
    event.getAllEvent({ keyword: "", specific_user: user.role_id === 7 ? user.id : null })
  );

  const listRole = useQuery("list-role", () => role.getAllRole({ specific_user: user.role_id === 7 ? user.id : null }));

  return (
    <MainLayout>
      <Breadcrumb
        label="Create User"
        breadcrumbs={[{ label: "Users", href: "/app/users" }]}
      />

      <ProtectedPermissionPage acceptPermissions={[7, 99]}>
        <Box sx={{ mt: 2 }}>
          <Grid container>
            <Grid item lg={4} xs={12}>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Username</FormLabel>
                <Controller
                  name="username"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Name</FormLabel>
                <Controller
                  name="name"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Phone Number</FormLabel>
                <Controller
                  name="phone_number"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>
              {showEvent && (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel>Event</FormLabel>
                  <Controller
                    name="event_id"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <Box>
                        <Select
                          {...field}
                          size="small"
                          error={Boolean(fieldState.error?.message)}
                          fullWidth
                        >
                          {get(listEvent, ["data", "data"], []).map(
                            (item: any, i: any) => (
                              <MenuItem value={item?.id} key={item?.id}>
                                {item?.name}
                              </MenuItem>
                            )
                          )}
                        </Select>

                        <FormHelperText error={Boolean(fieldState.error?.message)}>
                          {fieldState.error?.message}
                        </FormHelperText>
                      </Box>
                    )}
                  />
                </FormControl>
              )}
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Role</FormLabel>
                <Controller
                  name="role_id"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <Box>
                      <Select
                        {...field}
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                        fullWidth
                      >
                        {get(listRole, ["data", "data"], []).map(
                          (item: any, i: any) => (
                            <MenuItem value={item?.id} key={item?.id}>
                              {item?.name}
                            </MenuItem>
                          )
                        )}
                      </Select>

                      <FormHelperText error={Boolean(fieldState.error?.message)}>
                        {fieldState.error?.message}
                      </FormHelperText>
                    </Box>
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" spacing={1}>
            <LoadingButton
              variant="contained"
              onClick={form.handleSubmit(onSubmit)}
              loading={createUser.isLoading}
            >
              SAVE
            </LoadingButton>

            <Button variant="outlined" onClick={() => navigate(-1)}>
              CANCEL
            </Button>
          </Stack>
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
